import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Home,
  Vault,
  Key,
  ShieldCheck,
  FileWarning,
  Trash2,
  Settings,
  LogOut,
  Users,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/stores/auth-store';
import { ROUTES } from '@/constants/routes';
import { vaultShareService } from '@/services/vault-share-service';

const navItems = [
  { icon: Home, labelKey: 'nav.dashboard', to: ROUTES.DASHBOARD },
  { icon: Vault, labelKey: 'nav.vaults', to: ROUTES.VAULT },
  { icon: Key, labelKey: 'nav.generator', to: ROUTES.GENERATOR },
  { icon: ShieldCheck, labelKey: 'nav.authenticator', to: ROUTES.AUTHENTICATOR },
  { icon: FileWarning, labelKey: 'nav.security', to: ROUTES.SECURITY_REPORT },
  { icon: Trash2, labelKey: 'nav.trash', to: ROUTES.TRASH },
  { icon: Download, labelKey: 'nav.downloads', to: ROUTES.DOWNLOADS },
  { icon: Settings, labelKey: 'nav.settings', to: ROUTES.SETTINGS },
];

export function Sidebar() {
  const { t } = useTranslation();
  const { logout, lock, user } = useAuthStore();

  const { data: sharedData } = useQuery({
    queryKey: ['shared-with-me'],
    queryFn: () => vaultShareService.listSharedWithMe(),
    staleTime: 30000,
  });

  const pendingCount = sharedData?.shares?.filter(s => s.status === 'PENDING').length || 0;
  const activeCount = sharedData?.shares?.filter(s => s.status === 'ACCEPTED').length || 0;

  return (
    <aside className="fixed left-0 top-0 hidden h-screen w-[248px] flex-col overflow-y-auto bg-sidebar text-sidebar-foreground md:flex">
      {/* Logo Section */}
      <div className="px-5 pb-6 pt-5">
        <div className="flex items-center gap-3.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 ring-1 ring-accent/20">
          <img
            src="/logotipo.png"
            alt="Oneck Pass"
              className="h-8 w-8 object-contain"
          />
          </div>
          <div>
            <span className="block text-[17px] font-semibold tracking-[-0.025em] text-white">OneckPass</span>
            <span className="block text-[10px] font-medium uppercase tracking-[0.16em] text-sidebar-muted">{t('shell.secureWorkspace')}</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3">
        <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-muted">{t('shell.workspace')}</p>
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                'mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors duration-150',
                isActive
                  ? 'bg-accent/10 text-accent'
                  : 'text-sidebar-muted hover:bg-muted hover:text-white'
              )
            }
          >
            <item.icon className="h-[17px] w-[17px]" strokeWidth={1.8} />
            {t(item.labelKey)}
          </NavLink>
        ))}

        {/* Shared Vaults Section */}
        {(pendingCount > 0 || activeCount > 0) && (
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-sidebar-muted">
              {t('nav.sharedWithMe')}
            </p>
            <NavLink
              to={ROUTES.SETTINGS}
              state={{ tab: 'shares' }}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-between rounded-xl px-3 py-2.5 text-[13px] font-medium transition-colors duration-150',
                  isActive
                    ? 'bg-accent/10 text-accent'
                    : 'text-sidebar-muted hover:bg-muted hover:text-white'
                )
              }
            >
              <div className="flex items-center gap-3">
                <Users className="h-4 w-4" />
                <span>{activeCount} {t('nav.sharedVaults')}</span>
              </div>
              {pendingCount > 0 && (
                <Badge variant="highlight" className="ml-2">
                  {pendingCount}
                </Badge>
              )}
            </NavLink>
          </div>
        )}
      </nav>

      {/* Footer Actions */}
      <div className="mx-3 mb-3 rounded-2xl border border-white/10 bg-white/[0.045] p-2">
        <div className="mb-1 flex items-center gap-2.5 rounded-xl px-2 py-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/15 text-xs font-bold text-accent">
            {user?.email?.charAt(0).toUpperCase() || 'O'}
          </div>
          <div className="min-w-0">
            <p className="truncate text-xs font-medium text-white">{user?.email}</p>
            <p className="text-[10px] text-sidebar-muted">{t('shell.zeroKnowledgeVault')}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start rounded-lg text-sidebar-muted hover:bg-muted hover:text-white"
          onClick={() => lock()}
        >
          <Shield className="mr-2 h-4 w-4" />
          {t('auth.lock')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start rounded-lg text-sidebar-muted hover:bg-red-500/10 hover:text-red-300"
          onClick={() => logout()}
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('auth.logout')}
        </Button>
      </div>
    </aside>
  );
}
